/**
 * Demo-mode analytics data.
 *
 * Enable by setting VITE_DEMO_MODE=true in your .env.local.
 * Remove (or set to false) to restore live data.
 *
 * Every figure is derived from one deterministic daily series per platform,
 * so the cards, chart, platform rows and campaigns always agree.
 */

import type {
  ActivityItem,
  AdPlatform,
  CampaignSummary,
  ChartData,
  ChartMetric,
  DashboardMetrics,
  Highlight,
  PeriodSummary,
  Platform,
  SyncStatus,
  TimeRange,
  Trends,
} from '@/types/analytics';

const CURRENCY = 'USD';
const DAYS: Record<TimeRange, number> = { '7d': 7, '30d': 30, '90d': 90 };

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

interface Day { spend: number; impressions: number; clicks: number; leads: number; purchases: number }

/** Day `n` days ago for a platform. Deterministic: no Math.random. */
function day(platform: AdPlatform, n: number): Day {
  const t = (180 - n) / 180;
  const wobble = 1 + Math.sin(n * 0.9 + (platform === 'google' ? 2 : 0)) * 0.18;
  const scale = platform === 'meta' ? 1 : 0.45;
  const spend = +(95 * scale * (0.7 + t * 0.5) * wobble).toFixed(2);
  const impressions = Math.round(spend * (platform === 'meta' ? 78 : 41));
  const clicks = Math.round(impressions * (platform === 'meta' ? 0.021 : 0.034));
  const leads = Math.round(clicks * (platform === 'meta' ? 0.07 : 0.05));
  const purchases = platform === 'meta' && n % 4 === 0 ? 1 : 0;
  return { spend, impressions, clicks, leads, purchases };
}

const PLATFORMS: AdPlatform[] = ['meta', 'google'];

function summarize(platforms: AdPlatform[], from: number, to: number): PeriodSummary {
  let spend = 0, impressions = 0, clicks = 0, leads = 0, purchases = 0;
  platforms.forEach((p) => {
    for (let n = from; n <= to; n++) {
      const d = day(p, n);
      spend += d.spend; impressions += d.impressions; clicks += d.clicks;
      leads += d.leads; purchases += d.purchases;
    }
  });
  const conversions = leads + purchases;
  const value = purchases * 180;
  return {
    currency: CURRENCY,
    mixed_currency: false,
    spend: +spend.toFixed(2),
    impressions,
    clicks,
    conversions,
    conversions_value: value,
    results: { leads, purchases, registrations: 0, other: 0 },
    ctr: impressions ? +(clicks / impressions * 100).toFixed(2) : null,
    conversion_rate: clicks ? +(conversions / clicks * 100).toFixed(2) : null,
    cpc: clicks ? +(spend / clicks).toFixed(2) : null,
    cpm: impressions ? +(spend / impressions * 1000).toFixed(2) : null,
    cost_per_result: conversions ? +(spend / conversions).toFixed(2) : null,
    roas: value && spend ? +(value / spend).toFixed(2) : null,
    active_campaigns: platforms.length,
  };
}

function trends(cur: PeriodSummary, prev: PeriodSummary): Trends {
  const pct = (a: number | null, b: number | null) => (a === null || !b ? null : +((a - b) / b * 100).toFixed(1));
  return {
    spend: pct(cur.spend, prev.spend),
    impressions: pct(cur.impressions, prev.impressions),
    clicks: pct(cur.clicks, prev.clicks),
    conversions: pct(cur.conversions, prev.conversions),
    ctr: pct(cur.ctr, prev.ctr),
    cost_per_result: pct(cur.cost_per_result, prev.cost_per_result),
  };
}

function platformsFor(platform: Platform | string | undefined): AdPlatform[] {
  return !platform || platform === 'all' ? PLATFORMS : PLATFORMS.filter((p) => p === platform);
}

export function mockGetDashboard(timeRange: TimeRange, platform: Platform): DashboardMetrics {
  const days = DAYS[timeRange];
  const plats = platformsFor(platform);
  const cur = summarize(plats, 0, days - 1);
  const prev = summarize(plats, days, days * 2 - 1);
  const by_platform: DashboardMetrics['by_platform'] = {};
  if (platform === 'all') {
    PLATFORMS.forEach((p) => {
      const c = summarize([p], 0, days - 1);
      by_platform[p] = { ...c, active_campaigns: 1, tracking_gap: false, trends: trends(c, summarize([p], days, days * 2 - 1)) };
    });
  }
  return {
    summary: cur,
    trends: trends(cur, prev),
    by_platform,
    time_range: { start: isoDaysAgo(days - 1), end: isoDaysAgo(0) },
    has_data: true,
  };
}

export function mockGetCampaignsSummary(platform?: string): {
  campaigns: CampaignSummary[]; total: number; limit: number; offset: number;
} {
  const campaigns: CampaignSummary[] = platformsFor(platform).map((p) => {
    const s = summarize([p], 0, 29);
    const leads = s.results.leads;
    return {
      id: p === 'meta' ? '120245847574510669' : '21984456120',
      name: p === 'meta' ? 'Luxury Watch Launch — India' : 'Luxury Watch — Search',
      platform: p,
      status: 'active',
      delivering: true,
      objective: 'leads',
      result_type: 'leads',
      result_count: leads,
      cost_per_result: leads && s.spend ? +(s.spend / leads).toFixed(2) : null,
      currency: CURRENCY,
      spend: s.spend,
      impressions: s.impressions,
      clicks: s.clicks,
      conversions: s.conversions,
      results: s.results,
      ctr: s.ctr,
      roas: s.roas,
      conversation_id: null,
      last_updated: isoDaysAgo(0),
    };
  });
  return { campaigns, total: campaigns.length, limit: 20, offset: 0 };
}

export function mockGetChartData(metric: ChartMetric, timeRange: TimeRange, platform: Platform): ChartData {
  const days = DAYS[timeRange];
  const series = platformsFor(platform);
  const pick = (d: Day) =>
    metric === 'spend' ? d.spend
      : metric === 'results' ? d.leads + d.purchases
        : metric === 'leads' ? d.leads
          : metric === 'purchases' ? d.purchases
            : metric === 'clicks' ? d.clicks : d.impressions;
  const data = Array.from({ length: days }, (_, i) => {
    const n = days - 1 - i;
    const date = isoDaysAgo(n);
    const point: ChartData['data'][number] = {
      label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      date,
      value: 0,
    };
    series.forEach((p) => {
      const v = +pick(day(p, n)).toFixed(2);
      point[p] = v;
      point.value = +(point.value + v).toFixed(2);
    });
    return point;
  });
  return {
    metric, data, series, currency: CURRENCY, mixed_currency: false,
    time_range: { start: isoDaysAgo(days - 1), end: isoDaysAgo(0) },
  };
}

export function mockGetSyncStatus(): SyncStatus {
  const now = new Date();
  return {
    platforms: PLATFORMS.map((p, i) => ({
      platform: p,
      state: 'ok',
      connected: true,
      last_synced_at: new Date(now.getTime() - (12 + i * 20) * 60000).toISOString(),
      last_data_date: isoDaysAgo(0),
      message: null,
    })),
    checked_at: now.toISOString(),
  };
}

export function mockGetHighlights(platform: Platform): { highlights: Highlight[] } {
  const highlights: Highlight[] = [];
  if (platform === 'all') {
    const meta = summarize(['meta'], 0, 29);
    const google = summarize(['google'], 0, 29);
    if (meta.cost_per_result && google.cost_per_result) {
      const [cheap, dear] = meta.cost_per_result < google.cost_per_result
        ? [['meta', meta], ['google', google]] as const
        : [['google', google], ['meta', meta]] as const;
      highlights.push({
        kind: 'platform_cheaper', tone: 'info',
        platform: cheap[0], other_platform: dear[0],
        cost_per_result: cheap[1].cost_per_result!, other_cost_per_result: dear[1].cost_per_result!,
        pct_better: Math.round((1 - cheap[1].cost_per_result! / dear[1].cost_per_result!) * 100),
        currency: CURRENCY,
      });
    }
  }
  return { highlights };
}

// ── Activity ──────────────────────────────────────────────────────────────────

const MOCK_ACTIVITIES: ActivityItem[] = [
  {
    id: 'demo-1',
    type: 'campaign_published',
    severity: 'info',
    title: 'Campaign published',
    description: 'Luxury Watch Launch — India went live on Meta.',
    campaign_id: '120245847574510669',
    campaign_name: 'Luxury Watch Launch — India',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    read: true,
  },
];

export function mockGetRecentActivity(limit: number): {
  activities: ActivityItem[];
  unread_count: number;
} {
  const activities = MOCK_ACTIVITIES.slice(0, limit);
  return {
    activities,
    unread_count: activities.filter((a) => !a.read).length,
  };
}
